import React from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Database, Download, Upload, RefreshCw, HardDrive, 
  FileText, Users, Mail, Settings, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import api, { getAuthUser } from '@/lib/api';

export default function AdminBackup() {
  const [backupInfo, setBackupInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [exporting, setExporting] = React.useState(false);
  const [restoring, setRestoring] = React.useState(false);
  const [themeConfig, setThemeConfig] = React.useState({
    primary_color: '#B91C1C',
    secondary_color: '#1F2937',
    accent_color: '#059669',
    logo_url: '',
    hero_image_url: '',
    footer_text: ''
  });
  const currentUser = getAuthUser();

  React.useEffect(() => {
    fetchBackupInfo();
    fetchThemeConfig();
  }, []);

  const fetchBackupInfo = async () => {
    try {
      const response = await api.get('/backup/info');
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Error fetching backup info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchThemeConfig = async () => {
    try {
      const response = await api.get('/config/theme');
      if (response.data) {
        setThemeConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching theme config:', error);
    }
  };

  const handleExport = async (includeUsers = false) => {
    setExporting(true);
    try {
      const response = await api.get('/backup/export', {
        params: { include_users: includeUsers },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_doem_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar backup');
    } finally {
      setExporting(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm('Restaurar backup? Dados existentes serão atualizados.')) {
      e.target.value = '';
      return;
    }

    setRestoring(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/backup/restore?merge_mode=true', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Backup restaurado! ${response.data.results.edicoes_restored} edições, ${response.data.results.newsletter_restored} newsletter`);
      fetchBackupInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar backup');
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  const handleSaveTheme = async () => {
    try {
      await api.put('/config/theme', themeConfig);
      toast.success('Configurações de tema salvas!');
    } catch (error) {
      toast.error('Erro ao salvar tema');
    }
  };

  if (!currentUser?.is_master) {
    return (
      <AdminLayout>
        <Card>
          <CardContent className="py-12 text-center">
            <Database className="mx-auto mb-4 text-gray-400" size={48} />
            <h2 className="font-heading text-xl font-bold mb-2">Acesso Restrito</h2>
            <p className="text-gray-600">Apenas o usuário mestre pode acessar o sistema de backup.</p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-backup">
        <h1 className="font-heading text-3xl font-bold text-gray-900">Configurações do Sistema</h1>

        {/* Backup Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database size={20} className="text-[#B91C1C]" />
              Informações do Banco de Dados
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner"></div>
              </div>
            ) : backupInfo ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <FileText className="mx-auto mb-2 text-blue-600" size={24} />
                  <div className="text-2xl font-bold text-blue-700">{backupInfo.total_edicoes}</div>
                  <div className="text-sm text-blue-600">Edições</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <HardDrive className="mx-auto mb-2 text-green-600" size={24} />
                  <div className="text-2xl font-bold text-green-700">{backupInfo.total_publicacoes}</div>
                  <div className="text-sm text-green-600">Publicações</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <Mail className="mx-auto mb-2 text-purple-600" size={24} />
                  <div className="text-2xl font-bold text-purple-700">{backupInfo.total_newsletter}</div>
                  <div className="text-sm text-purple-600">Newsletter</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <Users className="mx-auto mb-2 text-orange-600" size={24} />
                  <div className="text-2xl font-bold text-orange-700">{backupInfo.total_users}</div>
                  <div className="text-sm text-orange-600">Usuários</div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center">Não foi possível carregar informações</p>
            )}
          </CardContent>
        </Card>

        {/* Backup & Restore */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Export */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download size={20} className="text-green-600" />
                Exportar Backup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-sm">
                Exporte todos os dados do DOEM para um arquivo JSON. Útil para migração ou backup de segurança.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={() => handleExport(false)}
                  disabled={exporting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {exporting ? <RefreshCw className="mr-2 animate-spin" size={16} /> : <Download className="mr-2" size={16} />}
                  Exportar Dados
                </Button>
                <Button 
                  onClick={() => handleExport(true)}
                  disabled={exporting}
                  variant="outline"
                  className="border-green-600 text-green-700 hover:bg-green-50"
                >
                  <Download className="mr-2" size={16} />
                  Com Usuários
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Restore */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload size={20} className="text-amber-600" />
                Restaurar Backup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-sm">
                Restaure dados de um arquivo de backup JSON exportado anteriormente.
              </p>
              <input
                type="file"
                accept=".json"
                id="backup-file"
                className="hidden"
                onChange={handleRestore}
                disabled={restoring}
              />
              <Button 
                onClick={() => document.getElementById('backup-file').click()}
                disabled={restoring}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {restoring ? <RefreshCw className="mr-2 animate-spin" size={16} /> : <Upload className="mr-2" size={16} />}
                Selecionar Arquivo
              </Button>
              <p className="text-xs text-gray-500">
                Modo merge: atualiza dados existentes e adiciona novos.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Theme Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette size={20} className="text-[#B91C1C]" />
              Configurações de Tema
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Cor Primária</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={themeConfig.primary_color}
                    onChange={(e) => setThemeConfig({...themeConfig, primary_color: e.target.value})}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={themeConfig.primary_color}
                    onChange={(e) => setThemeConfig({...themeConfig, primary_color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Cor Secundária</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={themeConfig.secondary_color}
                    onChange={(e) => setThemeConfig({...themeConfig, secondary_color: e.target.value})}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={themeConfig.secondary_color}
                    onChange={(e) => setThemeConfig({...themeConfig, secondary_color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Cor de Destaque</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={themeConfig.accent_color}
                    onChange={(e) => setThemeConfig({...themeConfig, accent_color: e.target.value})}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={themeConfig.accent_color}
                    onChange={(e) => setThemeConfig({...themeConfig, accent_color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>URL do Logo</Label>
              <Input
                type="text"
                value={themeConfig.logo_url}
                onChange={(e) => setThemeConfig({...themeConfig, logo_url: e.target.value})}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>URL da Imagem de Fundo (Hero)</Label>
              <Input
                type="text"
                value={themeConfig.hero_image_url}
                onChange={(e) => setThemeConfig({...themeConfig, hero_image_url: e.target.value})}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <Button onClick={handleSaveTheme} className="bg-[#B91C1C] hover:bg-[#991B1B]">
              <Settings size={16} className="mr-2" />
              Salvar Configurações
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
